/**
 * Runtime configuration for the Vite SPA.
 *
 * A single static bundle is deployed to every environment, so config is
 * resolved at runtime from `window.location.hostname` rather than baked in at
 * build time. Optional `VITE_*` build-time vars override the detected values —
 * this is how e2e (dynamic container port) points the SPA at a non-standard
 * backend. `.local.env` deliberately sets none of them so a locally-built deploy
 * artifact can't bake a `localhost` URL that would override the hostname map.
 * None of these values are secrets.
 */

export type EnvironmentName = 'local' | 'dev' | 'prod';

export type EnvironmentConfig = {
  /** Base URL of the public cloud-backend API. */
  cloudBackendUrl: string;
  /** Descope project ID for the user-facing (frontend) project. */
  descopeProjectId: string;
  /** Descope base URL (custom domain for the flow). */
  descopeBaseUrl: string;
};

export type ConfigOverrides = Partial<EnvironmentConfig>;

const CONFIG_BY_ENVIRONMENT: Record<EnvironmentName, EnvironmentConfig> = {
  local: {
    cloudBackendUrl: 'http://localhost:3000',
    descopeProjectId: 'P35JYmMOr5epmFvKC42KahVpRFPg',
    descopeBaseUrl: 'https://auth.home.cartesian-dev.click',
  },
  dev: {
    cloudBackendUrl: 'https://private-api.cartesian-dev.click',
    descopeProjectId: 'P35JYmMOr5epmFvKC42KahVpRFPg',
    descopeBaseUrl: 'https://auth.home.cartesian-dev.click',
  },
  prod: {
    cloudBackendUrl: 'https://private-api.cartesian.io',
    // eslint-disable-next-line @cspell/spellchecker
    descopeProjectId: 'P35MA2RIrSb4owUsx6jXXbrwtCf2',
    descopeBaseUrl: 'https://auth.home.cartesian.io',
  },
};

// Prod is reachable on both the vanity domain and the environment alias.
export function detectEnvironment(
  hostname: string,
): EnvironmentName | undefined {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
  if (hostname.endsWith('.cartesian-dev.click')) return 'dev';
  if (
    hostname.endsWith('.cartesian.io') ||
    hostname.endsWith('.cartesian-prod.click')
  ) {
    return 'prod';
  }
  return undefined;
}

// Treat an empty string the same as unset so a blank `VITE_*` can't mask the
// hostname fallback (Vite replaces undefined vars with `undefined`).
function override(value: string | undefined, fallback: string | undefined) {
  return value && value.length > 0 ? value : fallback;
}

/**
 * Pure resolver: hostname picks the base config, `overrides` (from `VITE_*`)
 * fill in or replace individual fields. Exported for unit testing — the runtime
 * call below wires in `window.location` and `import.meta.env`.
 */
export function resolveConfig(
  hostname: string,
  overrides: ConfigOverrides = {},
): EnvironmentConfig {
  const detected = detectEnvironment(hostname);
  const base = detected ? CONFIG_BY_ENVIRONMENT[detected] : undefined;

  const resolved: EnvironmentConfig = {
    cloudBackendUrl: override(
      overrides.cloudBackendUrl,
      base?.cloudBackendUrl,
    ) as string,
    descopeProjectId: override(
      overrides.descopeProjectId,
      base?.descopeProjectId,
    ) as string,
    descopeBaseUrl: override(
      overrides.descopeBaseUrl,
      base?.descopeBaseUrl,
    ) as string,
  };

  const missing = (
    Object.entries(resolved) as [keyof EnvironmentConfig, string | undefined][]
  )
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(
      `Unable to resolve Ecosystem Manager config for hostname "${hostname}": ` +
        `missing ${missing.join(', ')}. Set the matching VITE_* override or ` +
        `register this host in CONFIG_BY_ENVIRONMENT.`,
    );
  }

  return resolved;
}

const resolved = resolveConfig(window.location.hostname, {
  cloudBackendUrl: import.meta.env.VITE_CLOUD_BACKEND_URL,
  descopeProjectId: import.meta.env.VITE_DESCOPE_PROJECT_ID,
  descopeBaseUrl: import.meta.env.VITE_DESCOPE_BASE_URL,
});

export const config = {
  ...resolved,
  // Disabled only in e2e (`VITE_DESCOPE_AUTO_REFRESH=false`): the suite seeds a
  // fresh session token before every navigation, so the SDK's in-browser refresh
  // round-trip is redundant — and it intermittently stalls in CI, wedging
  // `isSessionLoading` until the 180s test timeout. On by default everywhere else.
  descopeAutoRefresh: import.meta.env.VITE_DESCOPE_AUTO_REFRESH !== 'false',
};

/**
 * Base URL of the Ecosystem Manager oRPC API. The oRPC `OpenAPILink` appends
 * each contract's `path` (e.g. `/api-key/list`) to this base, so it must include
 * the backend controller prefix (`ecosystem-manager-api`).
 */
export const orpcUrl = `${config.cloudBackendUrl}/ecosystem-manager-api`;
